import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import GlassCard from "../../../components/common/GlassCard";

function MedicationsCard({ medications, onAddMedication, onOpenLookup, onDeleteMedication }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeMedicationId, setActiveMedicationId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveMedicationId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveMedicationId(null);
  };

  return (
    <GlassCard
      title="Medications"
      icon={<LocalHospitalIcon color="primary" />}
      onAdd={onAddMedication}
      addLabel="Add Medication"
      onLookup={onOpenLookup}
      lookupLabel="Medication Lookup"
    >
      {medications?.length ? (
        medications.map((med) => (
          <Box
            key={med.id}
            mb={1}
            sx={{
              p: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
            data-testid={`medication-item-${med.id}`}
            data-medication-name={med.drug}
            data-medication-dose={med.dose}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box flex={1}>
                <Typography variant="body2" fontWeight={500}>
                  {med.drug}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {med.dose} • {med.frequency} • {med.route}
                </Typography>
              </Box>
              <Tooltip title="Options">
                <IconButton 
                  size="small" 
                  onClick={(e) => handleMenuOpen(e, med.id)}
                  data-testid={`medication-menu-button-${med.id}`}
                  aria-label={"Medication options"}
                  data-action="medication-menu"
                  data-medication-id={med.id}
                >
                  <MoreVertIcon 
                    aria-label={"Medication options"} 
                    fontSize="small"
                    data-testid={`medication-options-icon-${med.id}`}
                    data-action="medication-menu"
                    data-category="medications"
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No medications
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (activeMedicationId) {
              onDeleteMedication(activeMedicationId);
            }
          }}
          data-testid={`medication-delete-menu-item-${activeMedicationId}`}
          aria-label="Delete medication"
          data-action="delete-medication"
          data-medication-id={activeMedicationId}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

MedicationsCard.propTypes = {
  medications: PropTypes.array,
  onAddMedication: PropTypes.func.isRequired,
  onOpenLookup: PropTypes.func.isRequired,
  onDeleteMedication: PropTypes.func.isRequired,
};

MedicationsCard.defaultProps = {
  medications: [],
};

export default MedicationsCard;
